import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import { ChevronDownIcon, SearchIcon } from 'lucide-react';
import type { DropdownIndicatorProps, GroupBase, Props as ReactSelectProps } from 'react-select';
import ReactSelect, { components } from 'react-select';

import { ClearIndicator } from './react-select-clear-indicator';
import { componentsStyles } from './react-select-component-style';
import { newContainer } from './react-select-new-container';
import { newMultiValue } from './react-select-new-multi-value';
import type { LabeComponentProps } from './types';

import { Spinner } from '../../progress';

export type RSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<ReactSelectProps<Option, IsMulti, Group>, 'isDisabled' | 'getOptionLabel'> & {
  label?: ReactNode;
  labelProps?: LabeComponentProps;
  disabled?: boolean;
  getMultiValueLabel?: (option: Option) => string;
  getOptionLabel?: (option: Option) => ReactNode;
  showBorder?: boolean;
  noBorder?: boolean;
  menuClassName?: string;
  menuListClassName?: string;
  optionClassName?: string;
  singleValueClassName?: string;
  multiValueClassName?: string;
  getRef?: (ref: HTMLInputElement | null) => void;
  showSearchIcon?: boolean;
  inputClassName?: string;
  containerClassName?: string;
  valueContainerClassName?: string;
};

export function Select<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>({
  label,
  labelProps,
  disabled,
  menuClassName,
  menuListClassName,
  getMultiValueLabel,
  getOptionLabel,
  showBorder = false,
  noBorder = false,
  optionClassName = '',
  singleValueClassName = '',
  multiValueClassName = '',
  isClearable = true,
  className,
  onKeyDown,
  getRef,
  showSearchIcon = false,
  inputClassName,
  containerClassName,
  valueContainerClassName,
  ...props
}: RSelectProps<Option, IsMulti, Group>) {
  const selectRef = useRef(null);
  const ControlComponent = useMemo(
    () => newContainer({ label, labelProps, showBorder, noBorder, containerClassName }),
    [label, labelProps, showBorder, noBorder, containerClassName]
  );
  const MultiValueComponent = useMemo(() => newMultiValue({ getMultiValueLabel }), [getMultiValueLabel]);

  const styleClasses = useMemo(
    () =>
      componentsStyles(
        props.isMulti,
        disabled,
        menuClassName,
        menuListClassName,
        optionClassName,
        singleValueClassName,
        multiValueClassName,
        className,
        inputClassName,
        valueContainerClassName
      ),
    [
      props.isMulti,
      disabled,
      menuClassName,
      menuListClassName,
      optionClassName,
      singleValueClassName,
      multiValueClassName,
      className,
      inputClassName,
      valueContainerClassName,
    ]
  );

  function focusNextElement() {
    const focussableElements =
      'a:not([disabled]), button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';

    if (document.activeElement && 'form' in document.activeElement) {
      const formElement = document.activeElement.form as HTMLFormElement;
      const focussable = Array.from(formElement.querySelectorAll(focussableElements)).filter(
        (element) =>
          (element as HTMLElement).offsetWidth > 0 ||
          (element as HTMLElement).offsetHeight > 0 ||
          element === document.activeElement
      );

      const index = focussable.indexOf(document.activeElement);
      if (index > -1) {
        const nextElement = focussable[index + 1] || focussable[0];
        (nextElement as HTMLElement).focus();
      }
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      const activeElement = document.activeElement as HTMLInputElement | null;
      if (activeElement && activeElement.form) {
        event.preventDefault();
        focusNextElement();
      }
    }
    onKeyDown?.(event);
  };

  const CustomDropdownIndicator = (props: DropdownIndicatorProps<Option, IsMulti, Group>) => {
    return (
      <components.DropdownIndicator {...props}>
        {showSearchIcon ? <SearchIcon className="size-4" /> : <ChevronDownIcon className="size-4" />}
      </components.DropdownIndicator>
    );
  };

  useEffect(() => {
    if (getRef) {
      getRef(selectRef.current as HTMLInputElement | null);
    }
  }, [getRef]);

  return (
    <ReactSelect
      ref={selectRef}
      {...props}
      isDisabled={disabled}
      components={{
        Control: ControlComponent,
        LoadingIndicator,
        MultiValue: MultiValueComponent,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ClearIndicator,
        DropdownIndicator: CustomDropdownIndicator,
      }}
      classNames={styleClasses}
      menuShouldScrollIntoView={false}
      menuPlacement="auto"
      // placing in portal to avoid overflow issues
      menuPortalTarget={
        typeof props.menuPortalTarget === 'undefined'
          ? document.getElementById('portal-root') || document.body
          : props.menuPortalTarget
      }
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      openMenuOnFocus
      isClearable={isClearable}
      getOptionLabel={getOptionLabel as ReactSelectProps<Option, IsMulti, Group>['getOptionLabel']}
      onKeyDown={handleKeyDown}
    />
  );
}

export function LoadingIndicator() {
  return <Spinner size="xs" />;
}
